import { useState, useEffect } from 'react'
import { ArrowLeft, Brain, Clock, BarChart3 } from 'lucide-react'
import { SprintDetails, SprintReport } from '../pages/SprintReport'
import { SprintAnalyzer } from '../lib/sprintAnalyzer'

interface SprintAnalysisProgressProps {
  boardId: string
  sprintDetails: SprintDetails
  onComplete: (report: SprintReport) => void
  onBack: () => void
}

const analysisSteps = [
  {
    id: 'fetchingCards',
    title: 'Fetching Cards',
    description: 'Loading sprint data',
    icon: BarChart3
  },
  {
    id: 'analyzingComments',
    title: 'Analyzing Comments',
    description: 'Processing discussions and feedback',
    icon: Brain
  },
  {
    id: 'calculatingMetrics',
    title: 'Calculating Metrics',
    description: 'Velocity, cycle time, burndown',
    icon: Clock
  },
  {
    id: 'generatingInsights',
    title: 'Generating Insights',
    description: 'AI-powered analysis and recommendations',
    icon: Brain
  }
]

export default function SprintAnalysisProgress({ 
  boardId, 
  sprintDetails, 
  onComplete, 
  onBack 
}: SprintAnalysisProgressProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [progress, setProgress] = useState(0)
  const [stepDetails, setStepDetails] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const performAnalysis = async () => {
      try {
        setError(null)
        const analyzer = new SprintAnalyzer()
        
        // Start analysis
        const report = await analyzer.analyzeSprint(
          boardId, 
          sprintDetails.label, 
          sprintDetails
        )
        
        // Complete with real report
        onComplete(report)
      } catch (error) {
        console.error('Analysis failed:', error)
        setError('Analysis failed. Please try again.')
      }
    }

    // Start the analysis
    performAnalysis()
  }, [boardId, sprintDetails, onComplete])

  useEffect(() => {
    // Update progress based on current step
    const stepProgress = ((currentStep + 1) / analysisSteps.length) * 100
    setProgress(stepProgress)
  }, [currentStep])

  useEffect(() => {
    // Update step details
    const details = [
      `Fetching cards with "${sprintDetails.label}" label...`,
      'Processing comments and discussions...',
      'Calculating metrics and performance...',
      'Generating insights and recommendations...'
    ]
    setStepDetails(details[currentStep] || '')
  }, [currentStep, sprintDetails.label])

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Analyzing Sprint Data</h2>
        <p className="text-gray-600 mb-6">
          AI is analyzing your sprint data and generating comprehensive insights
        </p>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Progress</span>
          <span>{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Analysis Steps */}
      <div className="space-y-4">
        {analysisSteps.map((step, index) => {
          const Icon = step.icon
          const isActive = index === currentStep
          const isCompleted = index < currentStep
          
          return (
            <div
              key={step.id}
              className={`flex items-center space-x-4 p-4 rounded-lg border transition-all ${
                isActive
                  ? 'border-primary-500 bg-primary-50'
                  : isCompleted
                  ? 'border-green-200 bg-green-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isActive
                  ? 'bg-primary-500 text-white'
                  : isCompleted
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {isCompleted ? (
                  <span className="text-sm">✓</span>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              
              <div className="flex-1">
                <h3 className={`font-medium ${
                  isActive ? 'text-primary-700' : 'text-gray-700'
                }`}>
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600">{step.description}</p>
                {isActive && stepDetails && (
                  <p className="text-sm text-primary-600 mt-1">{stepDetails}</p>
                )}
              </div>
              
              {isActive && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
              )}
            </div>
          )
        })}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">!</span>
            </div>
            <div>
              <p className="text-sm font-medium text-red-900">Analysis Failed</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Estimated Time */}
      <div className="text-center text-sm text-gray-600">
        Analysis in progress... This may take a few minutes
      </div>

      {/* Back Button */}
      <div className="flex justify-start pt-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
      </div>
    </div>
  )
} 